// src/pages/StatusPage.tsx — Vanessa API Platform
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { VANESSA_API_URL } from '@/api/constants';
import { PageHeader, Section, Container } from '@/components/site/blocks';

interface HealthResponse {
    status: 'ok' | 'degraded';
    database: 'ok' | 'unreachable';
    anthropic: 'configured' | 'missing';
    timestamp: string;
}

export default function StatusPage() {
    const [health, setHealth] = useState<HealthResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastChecked, setLastChecked] = useState<Date | null>(null);

    const check = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const base = VANESSA_API_URL || 'https://api.kinemaplus.com';
            const response = await fetch(`${base}/v1/health`);
            const data = await response.json();
            setHealth(data);
        } catch {
            setError('Impossible de joindre le service.');
            setHealth(null);
        } finally {
            setLastChecked(new Date());
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        check();
        // Revérifie automatiquement toutes les 60 secondes, mais
        // SEULEMENT tant que l'onglet est réellement visible — inutile de
        // continuer à interroger le service si la personne a changé
        // d'onglet ou mis son écran en veille, ça ne fait que gonfler le
        // nombre d'exécutions pour rien.
        let interval: ReturnType<typeof setInterval> | null = null;

        const startPolling = () => {
            if (interval) return;
            check();
            interval = setInterval(check, 60_000);
        };
        const stopPolling = () => {
            if (interval) clearInterval(interval);
            interval = null;
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') startPolling();
            else stopPolling();
        };

        if (document.visibilityState === 'visible') startPolling();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [check]);

    const isUp = health?.status === 'ok';
    const isDown = !!error || health?.status === 'degraded';

    return (
        <>
            <PageHeader eyebrow="Développeurs" title="Statut de l’API" intro="Vérification en direct, toutes les 60 secondes." />
            <Section>
                <Container className="!px-0 max-w-xl">
                    <div
                        role="status"
                        className={`rounded-3xl border p-6 text-center ${
                            loading ? 'border-gray-200 bg-gray-50' : isUp ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                        }`}
                    >
                        <div className="mb-2 flex items-center justify-center gap-3">
                            {loading ? (
                                <Loader2 className="h-6 w-6 animate-spin text-gray-600" aria-hidden="true" />
                            ) : isUp ? (
                                <CheckCircle2 className="h-6 w-6 text-green-600" aria-hidden="true" />
                            ) : (
                                <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
                            )}
                            <p className="font-display text-xl font-bold text-ink">
                                {loading ? 'Vérification…' : isUp ? 'Tout fonctionne' : isDown ? 'Problème détecté' : 'Statut inconnu'}
                            </p>
                        </div>
                        {lastChecked && (
                            <p className="text-sm text-gray-600">Dernière vérification : {lastChecked.toLocaleTimeString('fr-FR')}</p>
                        )}
                    </div>

                    {health && (
                        <div className="mt-4 divide-y divide-gray-100 rounded-2xl border border-gray-200 bg-white">
                            <StatusRow label="Base de données" ok={health.database === 'ok'} />
                            <StatusRow label="Service IA" ok={health.anthropic === 'configured'} />
                        </div>
                    )}

                    {error && <p className="mt-4 text-center text-base text-red-600">{error}</p>}

                    <p className="mt-8 text-center text-base text-gray-700">
                        Un problème persistant ? Écrivez-nous depuis la{' '}
                        <Link to="/contact" className="font-semibold text-ochre underline">page contact</Link>.
                    </p>
                </Container>
            </Section>
        </>
    );
}

function StatusRow({ label, ok }: { label: string; ok: boolean }) {
    return (
        <div className="flex items-center justify-between px-5 py-3">
            <span className="text-base text-gray-700">{label}</span>
            <span className={`text-sm font-semibold ${ok ? 'text-green-700' : 'text-red-600'}`}>
                {ok ? 'Opérationnel' : 'Indisponible'}
            </span>
        </div>
    );
}
